/**
 * useProfileCompletion — derives a 0–100 completion score from the
 * authenticated profile, plus the list of remaining items the member
 * can act on. Used by the ProfileCompletionMeter card on /account.
 *
 * Each item is weighted to reflect how much it helps match quality
 * and trust signal. KYC + photos + prompts pull the most weight; voice
 * intro and BGC are bonus polish.
 */
import { useMemo } from "react";
import { useProfile } from "@/lib/profile/useProfile";
import { MIN_PROMPTS } from "@/lib/profile/types";

export interface CompletionItem {
  key: string;
  label: string;
  done: boolean;
  weight: number;
  href: string;
  hint?: string;
}

export interface CompletionState {
  loading: boolean;
  percent: number; // 0–100
  done: CompletionItem[];
  todo: CompletionItem[];
  /** Convenience: true once we hit 100 (eligible for the free Boost reward). */
  isComplete: boolean;
}

export function useProfileCompletion(): CompletionState {
  const { profile, loading } = useProfile();

  return useMemo<CompletionState>(() => {
    if (!profile) {
      return { loading, percent: 0, done: [], todo: [], isComplete: false };
    }

    const photoCount = profile.photos?.length ?? 0;
    const promptAnswers = (profile.prompts ?? []).filter((p) => p.answer?.trim()).length;

    const items: CompletionItem[] = [
      {
        key: "display_name",
        label: "Add a display name",
        done: !!profile.display_name?.trim(),
        weight: 5,
        href: "/account",
      },
      {
        key: "bio",
        label: "Write a short bio",
        done: !!profile.bio && profile.bio.trim().length >= 20,
        weight: 5,
        href: "/account",
        hint: "20+ characters",
      },
      {
        key: "pronouns",
        label: "Add your pronouns",
        done: !!profile.pronouns?.trim(),
        weight: 3,
        href: "/account",
      },
      {
        key: "location",
        label: "Add your location",
        done: !!profile.location_text?.trim(),
        weight: 4,
        href: "/account",
      },
      {
        key: "photos_one",
        label: "Upload your first photo",
        done: photoCount >= 1,
        weight: 10,
        href: "/account",
      },
      {
        key: "photos_three",
        label: "Add at least 3 photos",
        done: photoCount >= 3,
        weight: 8,
        href: "/account",
        hint: "3+ photos get 2× the matches",
      },
      {
        key: "prompts",
        label: `Answer ${MIN_PROMPTS} prompts`,
        done: promptAnswers >= MIN_PROMPTS,
        weight: 10,
        href: "/account",
      },
      {
        key: "voice_intro",
        label: "Record a voice intro",
        done: !!profile.voice_intro_url,
        weight: 6,
        href: "/account",
        hint: "Optional, but voice intros stand out",
      },
      {
        key: "video_intro",
        label: "Add a video intro",
        done: !!profile.video_intro_url,
        weight: 5,
        href: "/account",
      },
      {
        key: "lane",
        label: "Pick what you’re here for",
        done: !!profile.friendship_lane,
        weight: 4,
        href: "/account",
      },
      {
        key: "primary_app",
        label: "Choose your primary community",
        done: !!profile.primary_app,
        weight: 5,
        href: "/account",
      },
      {
        key: "kyc",
        label: "Verify your ID (KYC)",
        done: profile.kyc_status === "verified",
        weight: 15,
        href: "/account",
        hint: "Unlocks trust badges + matches",
      },
      {
        key: "emergency",
        label: "Set an emergency contact",
        done: !!profile.emergency_contact_name && !!profile.emergency_contact_phone,
        weight: 6,
        href: "/safety",
      },
      {
        key: "bgc",
        label: "Optional: Background check",
        done: !!profile.bgc_verified,
        weight: 8,
        href: "/safety",
        hint: "Premium trust signal",
      },
      {
        key: "username",
        label: "Claim your username",
        done: !!profile.username?.trim(),
        weight: 6,
        href: "/account",
      },
    ];

    const totalWeight = items.reduce((s, it) => s + it.weight, 0);
    const earned = items.filter((it) => it.done).reduce((s, it) => s + it.weight, 0);
    const percent = Math.round((earned / totalWeight) * 100);

    return {
      loading,
      percent,
      done: items.filter((it) => it.done),
      todo: items.filter((it) => !it.done),
      isComplete: percent >= 100,
    };
  }, [profile, loading]);
}
